/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useEffect } from 'react';
import { useSEOHead } from '../../hooks/useSEOHead';
import TradeInCalculator from './components/TradeInCalculator';
import TradeInComparison from './components/TradeInComparison';
import TradeInGuide from './components/TradeInGuide';
import TradeInTestimonials from './components/TradeInTestimonials';
import TradeInForm from './components/TradeInForm';
import TradeInHowItWorks from './components/TradeInHowItWorks';
import TradeInWhyUs from './components/TradeInWhyUs';
import TradeInFAQ from './components/TradeInFAQ';
import TradeInValueFactors from './components/TradeInValueFactors';
import { useSiteImages } from '../../hooks/useSiteImages';
import { useImagePreload } from '../../hooks/useImagePreload';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { pageFaqs } from '../../mocks/pageFaqs';

const OG_IMAGE = 'https://public.readdy.ai/ai/img_res/cc246073ad8f4a9001c90c8045981a95.jpg';

export default function TradeInPage() {
  const { getImageUrl } = useSiteImages();
  const heroUrl = getImageUrl('tradein-hero');
  const imageLoaded = useImagePreload(heroUrl);

  useSEOHead({
    title: 'Trade-In Value Calculator | Warrenton OR | Rockhound',
    description: 'Free trade-in calculator for Oregon Coast & SW Washington. Get instant estimates + official appraisals in 24 hours. Fair market value for your car, truck, or SUV.',
    keywords: 'trade in value calculator, car trade in value, trade in car near me, vehicle trade in value, trade in value estimator, KBB trade in value, NADA trade in value, trade in appraisal, trade in my car, trade in offer, Warrenton Oregon, Astoria, Seaside, Tillamook, Long Beach Washington, Longview, Kelso, Oregon Coast, SW Washington',
    canonicalUrl: 'https://rockhoundauto.com/trade-in',
    ogImage: OG_IMAGE,
    geoRegion: 'US-OR',
    geoPlacename: 'Warrenton, Oregon',
    geoPosition: '46.1651;-123.9237',
  });

  useEffect(() => {
    // ── FAQPage schema from pageFaqs.tradeIn ──────────────────────────────
    const faqPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': 'https://rockhoundauto.com/trade-in#faqpage-structured',
      mainEntity: pageFaqs.tradeIn.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    };

    // FAQ Schema
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': 'https://rockhoundauto.com/trade-in#faqpage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How much is my car worth in Oregon?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Your car\'s value in Oregon depends on make, model, year, mileage, condition, and current market demand. Use our free trade-in calculator above for an instant estimate based on real-time Oregon Coast market data. For an official appraisal, submit your vehicle details and we\'ll provide a competitive offer within 24 hours. We serve Warrenton, Astoria, Seaside, Tillamook, and all of SW Washington with fair market valuations.'
          }
        },
        {
          '@type': 'Question',
          name: 'Is it better to trade in or sell privately in Oregon?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Selling privately in Oregon typically nets 10-15% more than a trade-in, but requires weeks of effort listing, showing, negotiating, and handling paperwork. Trade-ins are instant, safe, and convenient — you drive away in your new vehicle the same day with no DMV hassles. If your time is valuable, trading in makes more sense. At Rockhound Auto in Warrenton, we offer fair market trade-in values without the lowball tactics used by traditional Oregon Coast dealerships.'
          }
        },
        {
          '@type': 'Question',
          name: 'What is my car worth in Astoria OR?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Vehicle values in Astoria, Oregon are determined by local market demand, seasonal trends, and regional preferences. Trucks and SUVs with all-wheel drive hold strong value on the Oregon Coast due to weather conditions. Use our calculator for an instant Astoria-area estimate, or submit your vehicle for an official appraisal. We\'re located just 10 minutes from Astoria in Warrenton and provide same-day valuations for Clatsop County residents.'
          }
        },
        {
          '@type': 'Question',
          name: 'How do I get the most for my trade-in on the Oregon Coast?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'To maximize your trade-in value on the Oregon Coast: clean your vehicle inside and out, gather all service records, fix minor issues like burned-out lights, address check engine lights, and be honest about condition. Get multiple appraisals from Warrenton, Astoria, and Seaside dealers. Rockhound Auto offers fair market value based on real data, not dealer profit margins. Timing matters too — trade in before major repairs are needed and avoid trading during slow winter months if possible.'
          }
        },
        {
          '@type': 'Question',
          name: 'What is the best trade-in value calculator for Oregon?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The best trade-in calculators for Oregon vehicles are Kelley Blue Book (KBB), NADA Guides, and Edmunds. Our Rockhound Auto calculator uses real-time Oregon Coast market data and typically matches or exceeds KBB trade-in values. For the most accurate estimate, use multiple calculators and compare offers from Warrenton, Astoria, and Portland-area dealers. Remember that online estimates are starting points — final offers depend on physical inspection and current inventory needs.'
          }
        },
        {
          '@type': 'Question',
          name: 'Can I trade in a car with a loan in Oregon?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, you can absolutely trade in a car with an outstanding loan in Oregon. If your trade-in value is higher than your loan payoff, the difference becomes your down payment. If you owe more than the car is worth (negative equity), that amount can be rolled into your new loan. We handle all payoff coordination with your lender — you don\'t need to pay off the loan before trading in. This is a common situation for Warrenton and Astoria customers, and we make the process seamless.'
          }
        },
        {
          '@type': 'Question',
          name: 'How long does a trade-in appraisal take at Rockhound Auto?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Our online trade-in appraisal form takes just 2 minutes to complete. You\'ll receive an initial offer within 24 hours. If you accept, bring your vehicle to our Warrenton location for a quick 15-20 minute physical inspection to verify condition. We confirm your offer on the spot — no surprises. The entire process from online submission to driving away in your new vehicle typically takes 2-3 days. Walk-ins are welcome, but scheduling an appointment ensures faster service.'
          }
        },
        {
          '@type': 'Question',
          name: 'What affects my car\'s trade-in value in Oregon?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Key factors affecting trade-in value in Oregon include: mileage (lower is better), overall condition (clean vehicles appraise higher), service history (documented maintenance adds value), accident history (clean Carfax increases value), tire condition, mechanical issues, body damage, interior wear, and current market demand. On the Oregon Coast, all-wheel drive, rust-free undercarriages, and good paint condition are especially important due to coastal weather. Seasonal demand also plays a role — convertibles sell better in summer, 4x4 trucks in winter.'
          }
        },
        {
          '@type': 'Question',
          name: 'Do you offer better trade-in values than Astoria Ford or Warrenton Kia?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Traditional dealerships like Astoria Ford, Warrenton Kia, and Lums Toyota lowball trade-ins to maximize profit margins — it\'s part of their business model. We\'re an auto broker, not a traditional dealer, so we don\'t have the same profit pressures. Our business is built on transparency and fair pricing. We offer market value for your trade-in based on real data, not what maximizes our profit. Compare our offer to any Oregon Coast dealer — we\'re confident you\'ll see the difference.'
          }
        },
        {
          '@type': 'Question',
          name: 'Where can I get a trade-in value estimate near Warrenton OR?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Rockhound Auto in Warrenton offers free instant trade-in estimates using our online calculator, plus official appraisals within 24 hours. We serve all of Clatsop County including Astoria, Seaside, Gearhart, and Cannon Beach, plus SW Washington cities like Long Beach, Ilwaco, Longview, and Kelso. Use our calculator above for an immediate estimate, or submit your vehicle details for a personalized appraisal. You can also call us at (855) 468-6348 or visit our Warrenton showroom at 1935 SE Ensign Ln for a same-day walk-in appraisal.'
          }
        }
      ]
    };

    // WebPage Schema
    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Trade-In Value Calculator',
      description: 'Free trade-in value calculator and appraisal service. Get fair market value for your vehicle on the Oregon Coast and SW Washington.',
      url: 'https://rockhoundauto.com/trade-in',
      image: {
        '@type': 'ImageObject',
        url: OG_IMAGE,
        width: 1200,
        height: 630,
      },
    };

    // BreadcrumbList Schema
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rockhoundauto.com' },
        { '@type': 'ListItem', position: 2, name: 'Trade-In Value', item: 'https://rockhoundauto.com/trade-in' },
      ],
    };

    // HowTo Schema
    const howToSchema = {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'How to Trade In Your Car at Rockhound Auto',
      description: 'Get top dollar for your trade-in in four simple steps. Rockhound Auto offers fair market value for vehicles on the Oregon Coast and SW Washington.',
      totalTime: 'P2D',
      estimatedCost: { '@type': 'MonetaryAmount', currency: 'USD', value: '0' },
      supply: [
        { '@type': 'HowToSupply', name: 'Vehicle title or payoff information' },
        { '@type': 'HowToSupply', name: 'Valid driver\'s license' },
        { '@type': 'HowToSupply', name: 'Current vehicle registration' },
        { '@type': 'HowToSupply', name: 'All keys and remotes' },
      ],
      step: [
        {
          '@type': 'HowToStep', position: 1, name: 'Enter Your Vehicle Info',
          text: 'Fill out our simple online form with your vehicle details — make, model, year, mileage, and condition. The form takes about 2 minutes to complete.',
          url: 'https://rockhoundauto.com/trade-in#step-1',
          image: { '@type': 'ImageObject', url: OG_IMAGE },
        },
        {
          '@type': 'HowToStep', position: 2, name: 'Receive Your Valuation',
          text: 'We evaluate your vehicle using real-time market data and provide a competitive trade-in offer within 24 hours — based on fair market value, not dealer profit margins.',
          url: 'https://rockhoundauto.com/trade-in#step-2',
          image: { '@type': 'ImageObject', url: OG_IMAGE },
        },
        {
          '@type': 'HowToStep', position: 3, name: 'Bring In Your Vehicle for Inspection',
          text: 'Bring your vehicle to our Warrenton, OR location for a quick physical inspection to verify condition. We confirm your offer on the spot — no surprises.',
          url: 'https://rockhoundauto.com/trade-in#step-3',
          image: { '@type': 'ImageObject', url: OG_IMAGE },
        },
        {
          '@type': 'HowToStep', position: 4, name: 'Complete the Deal',
          text: 'Apply your trade-in value toward your next vehicle purchase or receive a direct cash payment. The entire process from submission to completion typically takes 2–3 days.',
          url: 'https://rockhoundauto.com/trade-in#step-4',
          image: { '@type': 'ImageObject', url: OG_IMAGE },
        },
      ],
    };

    // Service Schema
    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://rockhoundauto.com/trade-in#service',
      name: 'Vehicle Trade-In Appraisal & Purchase',
      alternateName: 'Car Trade-In Service Oregon Coast',
      description: 'Free trade-in appraisals for cars, trucks, and SUVs on the Oregon Coast and SW Washington. Get a fair market value offer within 24 hours — no lowball tactics, no pressure. We serve Warrenton, Astoria, Seaside, Tillamook, Long Beach WA, and all of Clatsop County.',
      url: 'https://rockhoundauto.com/trade-in',
      serviceType: 'Vehicle Trade-In Appraisal',
      category: 'Automotive',
      provider: {
        '@type': 'AutoDealer',
        '@id': 'https://rockhoundauto.com/#localbusiness',
        name: 'Rockhound Auto',
        url: 'https://rockhoundauto.com',
        telephone: '(855) 468-6348',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '1935 SE Ensign Ln',
          addressLocality: 'Warrenton',
          addressRegion: 'OR',
          postalCode: '97146',
          addressCountry: 'US',
        },
      },
      areaServed: [
        { '@type': 'City', name: 'Warrenton', containedInPlace: { '@type': 'State', name: 'Oregon' } },
        { '@type': 'City', name: 'Astoria', containedInPlace: { '@type': 'State', name: 'Oregon' } },
        { '@type': 'City', name: 'Seaside', containedInPlace: { '@type': 'State', name: 'Oregon' } },
        { '@type': 'City', name: 'Tillamook', containedInPlace: { '@type': 'State', name: 'Oregon' } },
        { '@type': 'City', name: 'Long Beach', containedInPlace: { '@type': 'State', name: 'Washington' } },
        { '@type': 'City', name: 'Ilwaco', containedInPlace: { '@type': 'State', name: 'Washington' } },
        { '@type': 'City', name: 'Longview', containedInPlace: { '@type': 'State', name: 'Washington' } },
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Trade-In Services',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Instant Online Trade-In Estimate',
              description: 'Get an instant trade-in value estimate using our free online calculator. Based on real-time Oregon Coast market data.',
            },
            price: '0',
            priceCurrency: 'USD',
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Official 24-Hour Trade-In Appraisal',
              description: 'Submit your vehicle details and receive a competitive official trade-in offer within 24 hours.',
            },
            price: '0',
            priceCurrency: 'USD',
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'In-Person Vehicle Inspection & Offer Confirmation',
              description: 'Bring your vehicle to our Warrenton location for a 15–20 minute physical inspection. We confirm your offer on the spot — no surprises.',
            },
            price: '0',
            priceCurrency: 'USD',
          },
        ],
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: 'Free trade-in appraisal — no obligation, no pressure',
        availability: 'https://schema.org/InStock',
        validFrom: new Date().toISOString().split('T')[0],
      },
      termsOfService: 'https://rockhoundauto.com/terms-of-service',
      availableChannel: {
        '@type': 'ServiceChannel',
        serviceUrl: 'https://rockhoundauto.com/trade-in',
        servicePhone: '(855) 468-6348',
        serviceSmsNumber: '(855) 468-6348',
        availableLanguage: 'English',
        serviceLocation: {
          '@type': 'Place',
          name: 'Rockhound Auto — Warrenton, OR',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '1935 SE Ensign Ln',
            addressLocality: 'Warrenton',
            addressRegion: 'OR',
            postalCode: '97146',
            addressCountry: 'US',
          },
        },
      },
    };

    const injectJsonLd = (id: string, data: object) => {
      let script = document.querySelector(`script[type="application/ld+json"][data-schema="${id}"]`) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-schema', id);
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    };

    injectJsonLd('faq', faqSchema);
    injectJsonLd('faqpage-tradein', faqPageSchema);
    injectJsonLd('webpage', webPageSchema);
    injectJsonLd('breadcrumb-tradein', breadcrumbSchema);
    injectJsonLd('howto-tradein', howToSchema);
    injectJsonLd('service-tradein', serviceSchema);

    return () => {
      document.querySelector('script[type="application/ld+json"][data-schema="faq"]')?.remove();
      document.querySelector('script[type="application/ld+json"][data-schema="faqpage-tradein"]')?.remove();
      document.querySelector('script[type="application/ld+json"][data-schema="webpage"]')?.remove();
      document.querySelector('script[type="application/ld+json"][data-schema="breadcrumb-tradein"]')?.remove();
      document.querySelector('script[type="application/ld+json"][data-schema="howto-tradein"]')?.remove();
      document.querySelector('script[type="application/ld+json"][data-schema="service-tradein"]')?.remove();
    };
  }, []);

  return (
    <>
      <Breadcrumb items={[{ label: 'Trade-In' }]} />
      <>
        <div className="relative text-white overflow-hidden min-h-[55vh] sm:min-h-[50vh] flex items-center">
          {!imageLoaded && <div className="absolute inset-0 bg-gray-800 animate-pulse" />}
          <img src={heroUrl} alt="Trade-In Value Calculator" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`} width={1920} height={1080} fetchPriority="high" decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28 w-full">
            <div className="text-center flex flex-col items-center justify-center">
              <div className="inline-block bg-teal-600 text-white px-4 py-1.5 rounded-full text-xs font-semibold mb-4 whitespace-nowrap">No Lowballs. No Games. Just a Real Number.</div>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">Your Trade-In Is Worth More Than They're Telling You</h1>
              <p className="text-base sm:text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto mb-6 sm:mb-8">Traditional dealers use your trade-in as a negotiating chip. We use actual market data and give you a straight answer — usually within 24 hours.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="#trade-in-calculator" className="inline-flex items-center justify-center px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-calculator-line mr-2"></i>See What It's Actually Worth
                </a>
                <a href="#trade-in-form" className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer">Get My Official Appraisal</a>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Dealer Insight Block */}
          <div className="mb-16 bg-gradient-to-br from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-lg p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                  <i className="ri-price-tag-3-line text-2xl text-white"></i>
                </div>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-2">Dealer Insight</h2>
                <p className="text-gray-700 leading-relaxed">
                  Traditional dealers lowball trade-ins to maximize profit margins on new car sales. We don't sell new cars, so we have no incentive to undervalue your trade. Our offers reflect actual wholesale market values—not artificially deflated numbers designed to pad dealer profits. You'll see the difference immediately.
                </p>
              </div>
            </div>
          </div>

          <TradeInCalculator />
          <TradeInComparison />
          <TradeInGuide />
          <TradeInTestimonials />
          <div id="how-it-works"><TradeInHowItWorks /></div>
          <TradeInForm />
          <TradeInValueFactors />
          <TradeInFAQ />
          <TradeInWhyUs />
        </div>
      </>
    </>
  );
}